import { FC, useState, useCallback, useRef } from 'react'
import { FixedCropper, FixedCropperRef, CropperPreview, CropperPreviewRef, CropperTransitions, CropperImage, CropperState } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { Modal, Button, Slider, Space } from 'antd'
import { ViewType } from './models'
import styled from 'styled-components'

interface ImageCropperProps {
  visible: boolean
  imageUrl: string
  viewType: ViewType
  onCancel: () => void
  onCropComplete: (croppedImage: Blob) => void
}

interface PreviewState {
  state: CropperState | null;
  image: CropperImage | null;
  transitions: CropperTransitions | null;
  loading?: boolean;
  loaded?: boolean;
}

interface ImageCropperInlineProps {
  imageUrl: string
  viewType: ViewType
  onCropComplete: (croppedImage: Blob) => void
  onPreviewStateChange?: (previewState: PreviewState) => void
  className?: string
}

const CropperContainer = styled.div<{ containerHeight: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.containerHeight}px;
  background-color: #000;

  .cropper {
    width: 100%;
    height: 100%;
  }
`

const ControlsContainer = styled.div`
  padding: 16px;
  background-color: #fafafa;
`

const CropperStyles = styled.div<{ cropperHeight: number }>`
  .cropper {
    height: ${props => props.cropperHeight}px;
    background-color: #000;
  }
`

export const ImageCropperInline: FC<ImageCropperInlineProps> = ({
  imageUrl,
  viewType,
  onCropComplete,
  onPreviewStateChange,
  className
}) => {
  const cropperRef = useRef<FixedCropperRef>(null)

  const [previewState, setPreviewState] = useState<PreviewState>({
    state: null,
    image: null,
    transitions: null
  });

  // Calculate default size based on view type
  const getDefaultSize = () => {
    const hasHeader = viewType !== ViewType.FULLSCREEN_IMAGE
    if (hasHeader) {
      return { width: 640, height: 240 }
    }
    return { width: 640, height: 360 } // Full image
  }

  // Get cropper height based on view type
  const getCropperHeight = () => {
    return viewType === ViewType.FULLSCREEN_IMAGE ? 360 : 240
  }

  // Get container height (cropper height + some padding)
  const getContainerHeight = () => {
    return getCropperHeight() // Add some padding
  }

  const handleCropConfirm = useCallback(async () => {
    if (!cropperRef.current) return

    try {
      const canvas = cropperRef.current.getCanvas()
      if (!canvas) return

      canvas.toBlob(blob => {
        if (blob) {
          onCropComplete(blob)
        } else {
          console.error('Canvas is empty')
        }
      }, 'image/jpeg', 0.95)
    } catch (e) {
      console.error('Error cropping image:', e)
    }
  }, [onCropComplete])

  const onUpdate = () => {
    const newPreviewState = {
      state: cropperRef.current.getState(),
      image: cropperRef.current.getImage(),
      transitions: cropperRef.current.getTransitions(),
      loaded: cropperRef.current.isLoaded(),
      loading: cropperRef.current.isLoading(),
    };
    setPreviewState(newPreviewState);
    onPreviewStateChange?.(newPreviewState);

    // Automatically generate cropped image when the cropper state changes and image is loaded
    if (newPreviewState.loaded && !newPreviewState.loading) {
      handleCropConfirm();
    }
  };

  return (
    <CropperStyles className={className} cropperHeight={getCropperHeight()}>
      <CropperContainer containerHeight={getContainerHeight()}>
        <FixedCropper
          stencilSize={getDefaultSize()}
          stencilProps={{
            handlers: false,
            lines: false,
            movable: false,
            resizable: false,
          }}
          defaultCoordinates={{
            left: 0,
            top: 0,
            width: getDefaultSize().width,
            height: getDefaultSize().height,
          }}
          onUpdate={onUpdate}
          ref={cropperRef}
          src={imageUrl}
          defaultSize={getDefaultSize()}
        />
      </CropperContainer>
    </CropperStyles>
  )
}

export const CropperPreviewComponent: FC<{ previewState: PreviewState }> = ({ previewState }) => {
  const previewRef = useRef<CropperPreviewRef>(null);

  return (
    <CropperPreview
      ref={previewRef}
      className="preview"
      {...previewState}
    />
  )
}

const ImageCropper: FC<ImageCropperProps> = ({
  visible,
  imageUrl,
  viewType,
  onCancel,
  onCropComplete,
}) => {
  return (
    <Modal
      title="Kép vágása"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <Space>
          <Button onClick={onCancel}>Mégsem</Button>
          <Button type="primary" onClick={() => {
            // This will be handled by the inline component
          }}>
            Vágás alkalmazása
          </Button>
        </Space>
      }
    >
      <ImageCropperInline
        imageUrl={imageUrl}
        viewType={viewType}
        onCropComplete={onCropComplete}
      />
    </Modal>
  )
}

export default ImageCropper